import { ChangeDetectorRef, Component, computed, inject, input, signal, ViewChild, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser, NgComponentOutlet } from '@angular/common';
import { SafeUrlPipe } from '../../../../shared/pipes/safe-url.pipe';
import { Router } from '@angular/router';
import { PdfFormFillerComponent } from '../../../../features/pdf-form-filler/pdf-form-filler.component';
import { ISection, IDocumentType, ISeoDocument } from '../../../../core/models/documents.model';
import { IDoc } from '../../../../core/models/states.model';
import { IExternalCountryDetails } from '../../../../core/models/external-country.model';
import { Payment } from '../payment/payment';
import { Invoice } from '../invoice/invoice';
import { GetCountryDocuments } from '../../../../core/services/get-country-documents';
import { UsStatesService } from '../../../../core/services/us-states.service';
import { OrderProcessingService } from '../../../../core/services/order-processing';
import { OrderPayload, CartResponse, CreateFedexLabelPayload, IStateAddress, OrderShippingPayload } from '../../../../core/models/order-payload.model';
import { ShippingChoice } from '../../../../features/pdf-form-filler/models/fedex-shipping.model';
import { DomSanitizer } from '@angular/platform-browser';
import { dynamicComponents } from '../../../../core/widget/widget-registry';

import { StateService } from '../../../../core/services/state.service';
import { SeoService } from '../../../../core/services/seo.service';

@Component({
    selector: 'app-personal',
    standalone: true,
    imports: [CommonModule, PdfFormFillerComponent, Payment, Invoice, SafeUrlPipe, NgComponentOutlet],
    templateUrl: './personal.html',
    styleUrl: './personal.scss',
})
export class Personal {
    sectionsRows = input.required<ISection[][]>();
    children = input.required<IDocumentType[]>();
    ISeoDocument = input.required<ISeoDocument>();

    sidebarSectionInput = input<ISection | null>(null, { alias: 'sidebarSection' });

    private stateService = inject(StateService);
    readonly sidebarSection = computed(() => this.sidebarSectionInput() || this.stateService.sidebarSection());

    private sanitizer = inject(DomSanitizer);

    private platformId = inject(PLATFORM_ID);
    private getCountryDocsService = inject(GetCountryDocuments);
    usStatesService = inject(UsStatesService);
    private orderProcessingService = inject(OrderProcessingService);
    public router = inject(Router);
    private cdr = inject(ChangeDetectorRef);
    externalCountryDetails: IExternalCountryDetails | null = null;
    readonly stateDetails = this.usStatesService.currentStateDetails;

    readonly pageTitle = computed(() => {
        const rows = this.sectionsRows();
        if (rows && rows.length > 0) {
            for (const row of rows) {
                if (row && row.length > 0 && row[0].section_type === 'title') {
                    return (row[0].content || '').replace(/\$\w+/g, '').trim();
                }
            }
        }
        return '';
    });

    readonly matchingDoc = computed<IDoc | null>(() => {
        const docs = this.stateDetails()?.documents;
        if (!docs || docs.length === 0) return null;

        const title = this.pageTitle().toLowerCase();
        if (!title) return docs[0] || null;

        // 1. Direct check if document name is in page title
        let found = docs.find(doc => doc.name && title.includes(doc.name.toLowerCase()));

        if (!found) {
            // 2. Direct check if title is in document name
            found = docs.find(doc => doc.name && doc.name.toLowerCase().includes(title));
        }

        if (!found) {
            // 3. Keyword matching (words with >3 characters)
            found = docs.find(doc => {
                if (!doc.name) return false;
                const words = doc.name.toLowerCase().split(/\s+/);
                return words.some(w => w.length > 3 && title.includes(w));
            });
        }

        return found || docs[0] || null;
    });

    // Cart response stored in a signal for use later on this page
    cartResponse = signal<CartResponse | null>(null);
    cartLoading = signal<boolean>(false);
    cartError = signal<string | null>(null);

    fedexLoading = signal<boolean>(false);
    fedexError = signal<string | null>(null);

    showPaymentModal = signal<boolean>(false);
    paymentErrorMessage = signal<string | null>(null);
    fedexLabels = signal<string[]>([]);
    orderComplete = signal<boolean>(false);
    thankYouComplete = signal<boolean>(false);
    showUnfinishedOrderModal = signal<boolean>(false);
    pendingRequestNumber: string | null = null;
    private isBrowser = false;

    fullOrderData = signal<any>(null);
    invoiceData = signal<any>(null);

    readonly paymentCartData = computed(() => {
        const full = this.fullOrderData();
        const fullData = full?.data || full;
        if (fullData && fullData.cartItems) {
            return fullData;
        }
        const cart = this.cartResponse();
        return cart?.data || cart;
    });

    @ViewChild('pdfFormFiller') pdfFormFiller!: PdfFormFillerComponent;

    readonly pdfUrls = computed(() => this.stateDetails()?.pdf_links || null);
    // readonly pdfUrls = computed(() => ['./assets/states/Wisconsin/1.pdf']);

    readonly firstOffice = computed(() => {
        const details = this.stateDetails();

        if (details && details.offices && details.offices.length > 0) {
            return details.offices[0];
        }
        return null;
    })

    constructor() {
        this.isBrowser = isPlatformBrowser(this.platformId);
        if (this.isBrowser) {
            const savedCart = localStorage.getItem('cart_response');
            if (savedCart) {
                try {
                    const parsed = JSON.parse(savedCart);
                    const data = parsed?.data || parsed;
                    if (data?.requestNumber) {
                        this.pendingRequestNumber = data.requestNumber;
                    }
                    this.showUnfinishedOrderModal.set(true);
                } catch (e) {
                    console.error('Failed to parse cart_response', e);
                }
            }
        }

    }

    ngOnInit(): void {
        this.handleTheSeo();
    }

    private seoService = inject(SeoService);

    handleTheSeo(){
        const doc = this.ISeoDocument();
        if (doc) {
            // Apply the same variable replacements to SEO text fields
            const seoDoc: ISeoDocument = {
                ...doc,
                meta_title: this.removeHash(doc.meta_title || '') ,
                meta_description: this.removeHash(doc.meta_description || ''),
                meta_keywords: this.removeHash(doc.meta_keywords || ''),
            };

            // If the API returns a redirect URL, navigate there instead of rendering
            if (seoDoc.redirect_url && isPlatformBrowser(this.platformId)) {
                window.location.href = seoDoc.redirect_url;
                return;
            }

            this.seoService.setPageSeo(seoDoc);
            // this.seoData = seoDoc;
        }
    }

    removeHash(text: string) {
        if (!text) return '';
        const state = this.usStatesService.currentStateDetails();
        return text
            .replaceAll('#stateName', state?.name || '')
            .replaceAll('#stateApostilleFee', state?.apostille_fee != null ? String(state.apostille_fee) : '')
            .replaceAll('#stateApostilleDetails', state?.apostille_details || '')
            .replaceAll('#stateProcessingTime', state?.processing_time || '')
            .replaceAll('#stateExpeditedService', state?.expedited_service != null ? (state.expedited_service ? 'Yes' : 'No') : '')
            .replaceAll('#stateExpeditedFee', state?.expedited_fee != null ? String(state.expedited_fee) : '')
            .replaceAll('#stateAcceptedDocs', Array.isArray(state?.accepted_documents) ? state.accepted_documents.join(', ') : '')
            .replaceAll('#stateOfficialSources', Array.isArray(state?.official_sources) ? state.official_sources.join(', ') : '')
            .replaceAll('#statePaymentDetails', state?.payment_methods_details || '');
    }

    private async initPersonalCart() {
        const today = new Date();
        const formattedDate = `${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}/${today.getFullYear()}`;
        const hiddenInput = typeof document !== 'undefined' ? document.querySelector<HTMLInputElement>('input.orderId') : null;
        const dotNetCountryId = hiddenInput ? (Number(hiddenInput.dataset['dotnetcountryid']) || 75) : 75;
        const countryDocumentCaseId = hiddenInput ? (Number(hiddenInput.dataset['id']) || 13171) : 13171;

        const payload: OrderPayload = {
            order_type: 'Personal',
            dotNetCountryId: dotNetCountryId,
            documents: [
                {
                    countryDocumentCaseId: countryDocumentCaseId,
                    count: 1,
                    is_expedited: false,
                    'reg-days': formattedDate,
                    'exp-days': formattedDate,
                }
            ]
        };

        this.cartLoading.set(true);
        this.cartError.set(null);
        try {
            const res = await this.orderProcessingService.addToCart(payload);
            if (res) {
                this.cartResponse.set(res);
            }
        } catch (err: any) {
            this.cartError.set(err.message || 'Failed to add to cart');
            console.error('Error adding personal order to cart:', err);
        } finally {
            this.cartLoading.set(false);
        }
    }

    sortTheMail(rawText: string) {
        const flagKeys = Object.keys(dynamicComponents);
        const escapedKeys = flagKeys.map(k => k.replace(/\$/g, '\\$'));
        const splitter = new RegExp(`(${escapedKeys.join('|')})`);

        return rawText
            .split(splitter)
            .filter(Boolean)
            .map(slice => ({
                html: dynamicComponents[slice]
                    ? null
                    : this.sanitizer.bypassSecurityTrustHtml(slice),
                componentToBootUp: dynamicComponents[slice] ?? null
            }));
    }

    readonly formattedAddress = computed(() => {
        const office = this.firstOffice();
        if (!office) return '';

        let firstAddressPart = '';
        if (office.address) {
            const values = Object.values(office.address);
            if (values.length > 0) {
                let firstVal = values[0];
                try {
                    if (typeof firstVal === 'string' && firstVal.trim().startsWith('{')) {
                        const parsed = JSON.parse(firstVal);
                        firstVal = Object.values(parsed)[0] as string;
                    }
                } catch (e) {
                    // Ignore parse error
                }
                firstAddressPart = firstVal;
            }
        }

        const addressParts = firstAddressPart ? [firstAddressPart] : [];
        if (office.city) addressParts.push(office.city);
        if (office.zipcode) addressParts.push(office.zipcode);
        return addressParts.filter(p => !!p).join(', ');
    });

    readonly formattedPhone = computed(() => {
        const office = this.firstOffice();
        if (!office) return '';
        return office.phone ? Object.values(office.phone).filter(p => !!p).join(', ') : '';
    });

    async onShippingChoice(choice: ShippingChoice) {
        // Initialize the cart on-demand when the user chooses shipping
        if (!this.cartResponse()) {
            await this.initPersonalCart();
        }

        const cart = this.cartResponse();
        const cartId = cart?.data?.id;

        if (!cartId) {
            console.error('Cannot create shipping label: no cart ID available');
            return;
        }

        // Build state address from stateDetails office
        const office = this.firstOffice();
        let stateAddress: IStateAddress | undefined;
        if (office) {
            const addressVal = office.address ? Object.values(office.address)[0] as string : '';
            const phoneVal = office.phone ? Object.values(office.phone)[0] as string : '';
            stateAddress = {
                name: office.name || '',
                address: addressVal || '',
                city: office.city || '',
                state: this.stateDetails()?.name || '',
                zipcode: office.zipcode || '',
                phone: phoneVal || '',
            };
        }

        // Determine direction and priority from the choice
        const directions: { direction: string; priority: boolean }[] = [];

        if (choice.send) {
            directions.push({
                direction: choice.send === 'intl' ? 'outbound-international' : 'inbound',
                priority: choice.send === '1dayFrom',
            });
        }
        if (choice.receive) {
            directions.push({
                direction: choice.receive === 'intl' ? 'outbound-international' : 'outbound',
                priority: choice.receive === '1dayTo',
            });
        }

        this.fedexLoading.set(true);
        this.fedexError.set(null);

        try {
            const newLabels: string[] = [];
            let hasInbound = false;
            let hasOutbound = false;
            let hasOutboundIntl = false;

            for (const dir of directions) {
                let addrToUse: any = choice.userAddress;
                let isIntl = false;

                if (dir.direction === 'inbound' && choice.send === 'intl') {
                    addrToUse = choice.sendIntlAddress;
                    isIntl = true;
                } else if ((dir.direction === 'outbound' || dir.direction === 'outbound-international') && choice.receive === 'intl') {
                    addrToUse = choice.receiveIntlAddress;
                    isIntl = true;
                }

                let firstName = '';
                let lastName = '';
                let company = '';
                let phone = '';
                let address = '';
                let city = '';
                let state = '';
                let zipcode = '';
                let country = '';
                let email = '';

                if (isIntl && addrToUse) {
                    firstName = addrToUse.firstName || '';
                    lastName = addrToUse.lastName || '';
                    company = addrToUse.company || '';
                    phone = addrToUse.phone || '';
                    address = `${addrToUse.street || ''} ${addrToUse.address2 || ''}`.trim();
                    city = addrToUse.city || '';
                    state = addrToUse.province || '';
                    zipcode = addrToUse.zipcode || '';
                    country = addrToUse.country || '';
                    email = addrToUse.email || '';
                } else if (addrToUse) {
                    const nameParts = (addrToUse.fullName || '').split(' ');
                    firstName = nameParts[0] || '';
                    lastName = nameParts.slice(1).join(' ') || '';
                    phone = addrToUse.phone || '';
                    address = addrToUse.address || '';
                    city = addrToUse.city || '';
                    state = addrToUse.state || '';
                    zipcode = addrToUse.zipcode || '';
                    email = addrToUse.email || '';
                    country = 'US'; // Default for domestic
                }

                const payload: CreateFedexLabelPayload = {
                    shipping_direction: dir.direction,
                    priority_overnight: dir.priority,
                    updated_address: {
                        outside: isIntl ? '1' : '0',
                        individual: '1',
                        company: company,
                        firstname: firstName,
                        lastname: lastName,
                        phone: phone,
                        extension: null,
                        cellphone: null,
                        email: email,
                        address: address,
                        country: country,
                        city: city,
                        zipcode: zipcode,
                        state: state,
                        reference: '',
                    },
                    stateAddress,
                };

                const res = await this.orderProcessingService.createShippingLabel(cartId, payload);
                if (!res.success) {
                    throw new Error(res.error?.error?.message || res.error?.message || `Failed to create label for ${dir.direction}`);
                }

                if (dir.direction === 'inbound') hasInbound = true;
                if (dir.direction === 'outbound') hasOutbound = true;
                if (dir.direction === 'outbound-international') hasOutboundIntl = true;

                const fedexFiles = res.data?.fedex_files || res.data?.data?.fedex_files;
                if (Array.isArray(fedexFiles)) {
                    for (const file of fedexFiles) {
                        if (file.label_url) {
                            newLabels.push(file.label_url);
                        }
                    }
                } else if (fedexFiles?.label_url) {
                    newLabels.push(fedexFiles.label_url);
                } else {
                    // Fallback for older responses just in case
                    const fallbackUrl = res.data?.label_url || res.data?.data?.label_url;
                    if (fallbackUrl) {
                        newLabels.push(fallbackUrl);
                    }
                }
            }

            if (newLabels.length > 0) {
                this.fedexLabels.set(newLabels);
            }

            const isFedexChoice = choice.type === 'fedex';
            const shippingPayload: OrderShippingPayload = {
                shipping_method: isFedexChoice ? 'FedEx' : 'Own Shipping',
                has_inbound_fedex: hasInbound,
                has_outbound_fedex: hasOutbound,
                has_outbound_international_fedex: hasOutboundIntl,
                shipping_file: "s"
            };

            const shipRes = await this.orderProcessingService.saveOrderShipping(cartId, shippingPayload);
            if (!shipRes.success) {
                throw new Error(shipRes.error?.error?.message || shipRes.error?.message || `Failed to save shipping details.`);
            }
            this.fullOrderData.set(shipRes.data || shipRes);

            // If all succeed, close the modal
            if (this.pdfFormFiller) {
                this.pdfFormFiller.onFedexClose(true);
                if (!isFedexChoice) {
                    this.pdfFormFiller.onPaymentClose(true);
                }
            }

        } catch (err: any) {
            console.error(`[Personal] FedEx label error:`, err);
            this.fedexError.set(err.message || 'An error occurred while creating FedEx labels.');
        } finally {
            this.fedexLoading.set(false);
        }
    }

    handlePaymentRequest(isFedex: boolean) {
        if (isFedex) {
            this.showPaymentModal.set(true);
            this.paymentErrorMessage.set(null);
        } else {
            if (this.pdfFormFiller) {
                this.pdfFormFiller.onPaymentClose(true);
            }
        }
    }

    onPaymentSuccess(res: any) {
        this.showPaymentModal.set(false);
        this.invoiceData.set(res);
        if (this.pdfFormFiller) {
            this.pdfFormFiller.onPaymentClose(true);
        }
    }

    onPaymentError(err: string) {
        this.paymentErrorMessage.set(err);
    }

    onPdfComplete() {
        const isFedex = this.fedexLabels().length > 0;
        if (isFedex) {
            this.orderComplete.set(true);
        } else {
            this.thankYouComplete.set(true);
        }
    }

    goToCart() {
        this.router.navigate(['/cart']);
    }

    async destroyPendingCart() {
        if (this.pendingRequestNumber) {
            try {
                await this.orderProcessingService.destroyCart(this.pendingRequestNumber);
            } catch (e) {
                console.error('Failed to destroy pending cart', e);
            }
        }
        if (this.isBrowser) {
            localStorage.removeItem('cart_response');
            localStorage.removeItem('full_order');
        }
        this.showUnfinishedOrderModal.set(false);
    }

    parseFaq(content: any): any[] {
        if (!content) return [];
        if (Array.isArray(content)) return content;
        try {
            let parsed = typeof content === 'string' ? JSON.parse(content) : content;
            if (typeof parsed === 'string') {
                parsed = JSON.parse(parsed);
            }
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            console.error('Error parsing FAQ content:', e);
            return [];
        }
    }
}
